// src/components/ui/EarningsTrendChart.jsx
// Reusable 30-day earnings area chart. Same visual language as the
// Overview / Dashboard trend chart so the platform pages feel consistent.
//
// Usage:
//   <EarningsTrendChart userId={user.id} table="adsense_daily" label="AdSense" />
//   <EarningsTrendChart userId={user.id} table="admanager_daily" label="Ad Manager" />
//
// Self-fetches its own 30-day window via Supabase, so callers don't need to
// pre-load anything. Renders nothing when there's no data — keeps the page
// clean for empty/never-fetched accounts.

import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { TrendingUp } from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from 'recharts';

// Tooltip matches the Dashboard chart's style.
function ChartTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card text-white text-xs rounded-xl px-3 py-2 shadow-xl border border-border">
      <p className="font-semibold mb-1 text-muted-foreground/50">{label}</p>
      {payload.map(p => (
        <p key={p.name} style={{ color: p.color }} className="font-medium">
          {p.name}: ${Number(p.value || 0).toFixed(2)}
        </p>
      ))}
    </div>
  );
}

export function EarningsTrendChart({
  userId,
  table,
  label = 'Earnings',
  days = 30,
  // Allow callers to override the brand color (defaults to emerald)
  color = '#10b981',
  // The column on the row that contains the daily net earnings amount.
  valueColumn = 'net_revenue',
  className = '',
}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      // Window: [today - (days-1), today]  inclusive
      const today = new Date();
      const fromDate = new Date(today);
      fromDate.setDate(today.getDate() - (days - 1));
      const fmt = d => d.toISOString().split('T')[0];

      const { data: rows, error } = await supabase
        .from(table)
        .select(`date, ${valueColumn}`)
        .eq('user_id', userId)
        .gte('date', fmt(fromDate))
        .lte('date', fmt(today))
        .order('date', { ascending: true });

      if (cancelled) return;
      if (error) {
        console.warn(`[EarningsTrendChart] ${table} load failed:`, error.message);
        setData([]);
        setLoading(false);
        return;
      }

      // Build a full date map so missing days render as zero (otherwise the
      // chart has gaps and the visual story is misleading).
      const dateMap = {};
      for (let i = days - 1; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(today.getDate() - i);
        const key = fmt(d);
        dateMap[key] = {
          date: key.slice(5),       // "MM-DD" for short axis labels
          fullDate: key,            // kept for tooltip
          value: 0,
        };
      }
      (rows || []).forEach(r => {
        if (dateMap[r.date]) {
          dateMap[r.date].value = parseFloat(r[valueColumn] || 0);
        }
      });

      setData(Object.values(dateMap));
      setLoading(false);
    };

    load();
    return () => { cancelled = true; };
  }, [userId, table, days, valueColumn]);

  // Hide the whole card when there's literally no data — keeps the page
  // clean for accounts that haven't fetched yet.
  const hasData = data.some(d => d.value > 0);
  if (loading) {
    return (
      <div className={`bg-card rounded-2xl border border-border p-5 ${className}`}>
        <div className="h-[200px] bg-muted/30 rounded-xl animate-pulse" />
      </div>
    );
  }
  if (!hasData) return null;

  const gradientId = `g-trend-${label.replace(/\s+/g, '-').toLowerCase()}`;

  return (
    <div className={`bg-card rounded-2xl border border-border shadow-sm p-5 ${className}`}>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-sm font-bold text-foreground">{days}-Day Earnings Trend</h2>
          <p className="text-xs text-muted-foreground/70 mt-0.5">{label}</p>
        </div>
        <TrendingUp className="h-5 w-5" style={{ color }} />
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <AreaChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor={color} stopOpacity={0.25} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: '#9ca3af' }}
            axisLine={false}
            tickLine={false}
            // On 30-day windows we only show every 5th label so the axis stays readable.
            interval={Math.max(0, Math.floor(days / 6) - 1)}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#9ca3af' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={v => `$${v >= 1000 ? (v/1000).toFixed(0)+'k' : v.toFixed(0)}`}
            width={45}
          />
          <Tooltip content={<ChartTip />} />
          <Area
            type="monotone"
            dataKey="value"
            name={label}
            stroke={color}
            strokeWidth={2}
            fill={`url(#${gradientId})`}
            dot={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export default EarningsTrendChart;
